/**
 * SIP Registrations Page
 * 
 * Monitor and manage SIP user registrations.
 * Uses common components for consistent UI patterns.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Users, UserCheck, UserX, Clock } from 'lucide-react';
import { api } from '../../services/api';
import { RegistrationsList } from '../../components/registrations/RegistrationsList';
import { RegistrationActions } from '../../components/registrations/RegistrationActions';
import { RegistrationDetailModal } from '../../components/registrations/RegistrationDetailModal';
import type { Registration, RegistrationMetadata } from '../../types/registration';
import {
  PageHeader,
  StatsGrid,
  StatCard,
  DeleteConfirmModal,
} from '../../components/common';

export function SipRegistrationsPage() {
  // Filter state
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [limit, setLimit] = useState(100);
  const [aorSearch, setAorSearch] = useState('');
  const [transportFilter, setTransportFilter] = useState<'' | 'udp' | 'tcp' | 'tls'>('');
  const [authenticatedFilter, setAuthenticatedFilter] = useState<'' | 'yes' | 'no'>('');
  
  // Modal/dialog state
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch registrations
  const { data: registrationsData, isLoading: registrationsLoading, error: registrationsError, isFetching } = useQuery({
    queryKey: ['sip-registrations', { domain: selectedDomain, activeOnly, limit }],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = { limit };
      if (selectedDomain) params.domain = selectedDomain;
      if (activeOnly) params.activeOnly = true;
      
      const response = await api.getSipRegistrations(params);
      return response;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch statistics (includes domain and transport breakdowns)
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['sip-registration-statistics'],
    queryFn: () => api.getSipRegistrationStatistics(),
    refetchInterval: 30000,
  });

  // Cleanup mutation
  const cleanupMutation = useMutation({
    mutationFn: () => api.cleanupExpiredSipRegistrations(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-registration-statistics'] });
    },
  });

  // Delete registration mutation
  const deleteMutation = useMutation({
    mutationFn: (registrationId: string) => api.deleteSipRegistration(registrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['sip-registration-statistics'] });
      setShowDeleteDialog(false);
      setShowDetailModal(false);
      setSelectedRegistration(null);
    },
  });

  // Apply client-side filters (AOR search, transport, authenticated)
  const allRegistrations: Registration[] = registrationsData?.data || [];
  
  const registrations = allRegistrations.filter((reg) => {
    // AOR search filter
    if (aorSearch) {
      const searchLower = aorSearch.toLowerCase();
      const matchesAor = reg.aor.toLowerCase().includes(searchLower);
      const matchesUsername = reg.username.toLowerCase().includes(searchLower);
      const matchesDomain = reg.domain.toLowerCase().includes(searchLower);
      if (!matchesAor && !matchesUsername && !matchesDomain) {
        return false;
      }
    }
    
    // Transport filter
    if (transportFilter && reg.transport !== transportFilter) {
      return false;
    }
    
    // Authenticated filter
    if (authenticatedFilter === 'yes' && !reg.authenticated) {
      return false;
    }
    if (authenticatedFilter === 'no' && reg.authenticated) {
      return false;
    }
    
    return true;
  });
  
  const metadata: RegistrationMetadata = {
    total: registrations.length,
    active: registrations.filter(r => r.isActive).length,
    expired: registrations.filter(r => !r.isActive).length,
    limit: limit,
    hasMore: allRegistrations.length >= limit
  };

  const stats = statsData?.data;

  // Handler functions
  const handleCleanup = () => {
    cleanupMutation.mutate();
  };

  const handleViewDetails = (registration: Registration) => {
    setSelectedRegistration(registration);
    setShowDetailModal(true);
  };

  const handleDeleteClick = (registration: Registration) => {
    setSelectedRegistration(registration);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRegistration) {
      deleteMutation.mutate(selectedRegistration.registrationId);
    }
  };

  const availableDomains = Object.keys(stats?.byDomain || {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="SIP Registrations"
        description="Monitor and manage SIP user registrations"
        onRefresh={() => queryClient.invalidateQueries({ queryKey: ['sip-registrations'] })}
        isRefreshing={isFetching}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Registrations"
          value={statsLoading ? '-' : (stats?.total || 0)}
          icon={Users}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Active"
          value={statsLoading ? '-' : (stats?.active || 0)}
          icon={UserCheck}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Expired"
          value={statsLoading ? '-' : (stats?.expired || 0)}
          icon={UserX}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
          valueColor="text-red-600"
        />
        <StatCard
          label="Avg TTL"
          value={statsLoading ? '-' : (stats?.averageTtl ? `${Math.round(stats.averageTtl)}s` : '-')}
          icon={Clock}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
      </StatsGrid>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap gap-4 items-end">
          {/* AOR Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search AOR / Username</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={aorSearch}
                onChange={(e) => setAorSearch(e.target.value)}
                placeholder="Search by AOR, username, or domain..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Domain filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Domains</option>
              {availableDomains.map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={activeOnly ? 'active' : 'all'}
              onChange={(e) => setActiveOnly(e.target.value === 'active')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active Only</option>
              <option value="all">All Registrations</option>
            </select>
          </div>

          {/* Transport filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transport</label>
            <select
              value={transportFilter}
              onChange={(e) => setTransportFilter(e.target.value as '' | 'udp' | 'tcp' | 'tls')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Transports</option>
              <option value="udp">UDP</option>
              <option value="tcp">TCP</option>
              <option value="tls">TLS</option>
            </select>
          </div>

          {/* Authenticated filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Authentication</label>
            <select
              value={authenticatedFilter}
              onChange={(e) => setAuthenticatedFilter(e.target.value as '' | 'yes' | 'no')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="yes">Authenticated</option>
              <option value="no">Not Authenticated</option>
            </select>
          </div>

          {/* Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Active filters summary */}
        {(aorSearch || transportFilter || authenticatedFilter) && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">Active filters:</span>
            {aorSearch && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{aorSearch}"
                <button 
                  onClick={() => setAorSearch('')}
                  className="ml-1 hover:text-blue-600"
                >
                  &times;
                </button>
              </span>
            )}
            {transportFilter && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Transport: {transportFilter.toUpperCase()}
                <button 
                  onClick={() => setTransportFilter('')}
                  className="ml-1 hover:text-green-600"
                >
                  &times;
                </button>
              </span>
            )}
            {authenticatedFilter && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {authenticatedFilter === 'yes' ? 'Authenticated' : 'Not Authenticated'}
                <button 
                  onClick={() => setAuthenticatedFilter('')}
                  className="ml-1 hover:text-purple-600"
                >
                  &times;
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setAorSearch('');
                setTransportFilter('');
                setAuthenticatedFilter('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <RegistrationActions
        onCleanup={handleCleanup}
        cleanupLoading={cleanupMutation.isPending}
      />

      {/* Registrations List */}
      <RegistrationsList
        registrations={registrations}
        metadata={metadata}
        loading={registrationsLoading}
        error={registrationsError}
        onViewDetails={handleViewDetails}
        onDelete={handleDeleteClick}
      />

      {/* Results Summary */}
      {!registrationsLoading && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>
              Showing {registrations.length} of {allRegistrations.length} registrations
              {selectedDomain && ` in domain "${selectedDomain}"`}
              {activeOnly && ' (active only)'}
              {(aorSearch || transportFilter || authenticatedFilter) && ' (filtered)'}
            </span>
            {metadata.hasMore && (
              <span className="text-blue-600">
                Increase limit to see more results
              </span>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRegistration && (
        <RegistrationDetailModal
          registration={selectedRegistration}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedRegistration(null);
          }}
          onDelete={() => {
            setShowDetailModal(false);
            setShowDeleteDialog(true);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={showDeleteDialog && !!selectedRegistration}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedRegistration(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Registration"
        entityType="registration"
        entityName={selectedRegistration ? `${selectedRegistration.username}@${selectedRegistration.domain}` : ''}
        additionalInfo={selectedRegistration && (
          <p className="text-sm text-gray-600 mt-2">AOR: {selectedRegistration.aor}</p>
        )}
        warningMessage="The user will need to re-register to use the service."
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}
